
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { KeyAsk, Account } from "../types";

export const useKeyAsks = (selectedAccount: Account | null) => {
  const [keyAsks, setKeyAsks] = useState<KeyAsk[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (selectedAccount?.company_id) {
      fetchKeyAsks();
    }
  }, [selectedAccount]);

  const fetchKeyAsks = async () => {
    if (!selectedAccount?.company_id) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('key_asks')
        .select('*')
        .eq('company_id', selectedAccount.company_id);
      
      if (error) throw error;
      setKeyAsks(data || []);
    } catch (error) {
      console.error('Error fetching key asks:', error);
      toast.error('Failed to load key asks');
    } finally {
      setIsLoading(false);
    }
  };

  const addKeyAsk = async (askData: Omit<KeyAsk, 'id'>) => {
    if (!selectedAccount?.company_id) {
      toast.error('No account selected');
      return false;
    }

    try {
      const { error } = await supabase
        .from('key_asks')
        .insert([{
          company_id: selectedAccount.company_id,
          ...askData
        }]);
      
      if (error) throw error;
      toast.success('Key ask added successfully');
      fetchKeyAsks();
      return true;
    } catch (error) {
      console.error('Error adding key ask:', error);
      toast.error('Failed to add key ask');
      return false;
    }
  };

  const updateKeyAsk = async (id: string, askData: Omit<KeyAsk, 'id'>) => {
    try {
      const { error } = await supabase
        .from('key_asks')
        .update(askData)
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Key ask updated successfully');
      fetchKeyAsks();
      return true;
    } catch (error) {
      console.error('Error updating key ask:', error);
      toast.error('Failed to update key ask');
      return false;
    }
  };

  const deleteKeyAsk = async (id: string) => {
    try {
      const { error } = await supabase
        .from('key_asks')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Key ask deleted successfully');
      fetchKeyAsks();
      return true;
    } catch (error) {
      console.error('Error deleting key ask:', error);
      toast.error('Failed to delete key ask');
      return false;
    }
  };

  return {
    keyAsks,
    isLoading,
    addKeyAsk,
    updateKeyAsk,
    deleteKeyAsk
  };
};
